import React, { useState, useRef } from "react";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../ui/card";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import { getStudentFiles, uploadFile, deleteFile, updateFileCategory } from "../../lib/api";
import { triggerBlobDownload } from "../../lib/download";

const FILE_CATEGORIES = [
  { value: "iep", label: "IEP" },
  { value: "contract", label: "Contract" },
  { value: "progress_report", label: "Progress Report" },
  { value: "proficiency", label: "Proficiency Report" },
  { value: "clinical_report", label: "Clinical Report" },
  { value: "supporting_doc", label: "Supporting Document" },
];

export function FilesTab({ student, editing, editData, setEditData, files, setFiles, assessments, studentId }) {
  const fileInputRef = useRef(null);
  const [uploadType, setUploadType] = useState("iep");
  const [viewingFile, setViewingFile] = useState(null);
  const [reclassifyFile, setReclassifyFile] = useState(null);
  const [reclassifyType, setReclassifyType] = useState("");
  const [savingReclassify, setSavingReclassify] = useState(false);

  // Opening authenticated files reliably: a real <a target="_blank"> click is
  // treated as a genuine navigation and is NOT blocked by popup blockers/Safari
  // the way window.open() is inside event handlers.
  const openInNewTab = (url) => {
    if (!url) return;
    const a = document.createElement("a");
    a.href = url;
    a.target = "_blank";
    a.rel = "noopener noreferrer";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    try {
      await uploadFile(studentId, uploadType, file);
      toast.success("File uploaded successfully");
      const filesRes = await getStudentFiles(studentId);
      setFiles(filesRes.data);
    } catch (err) {
      toast.error("Failed to upload file");
    }
    e.target.value = "";
  };

  const handleDownload = async (fileId, filename) => {
    try {
      const token = localStorage.getItem("token");
      const baseUrl = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${baseUrl}/api/files/download/${fileId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.ok) throw new Error("Download failed");
      const blob = await response.blob();
      triggerBlobDownload(blob, filename);
    } catch (err) {
      toast.error("Failed to download file");
    }
  };

  const handleDeleteFile = async (fileId) => {
    if (!window.confirm("Are you sure you want to delete this file?")) return;
    try {
      await deleteFile(fileId);
      toast.success("File deleted");
      const filesRes = await getStudentFiles(studentId);
      setFiles(filesRes.data);
    } catch (err) {
      toast.error("Failed to delete file");
    }
  };

  const openReclassify = (file) => {
    setReclassifyFile(file);
    setReclassifyType(file.file_type);
  };

  const handleReclassify = async () => {
    if (!reclassifyFile || !reclassifyType || reclassifyType === reclassifyFile.file_type) {
      setReclassifyFile(null);
      return;
    }
    setSavingReclassify(true);
    try {
      await updateFileCategory(reclassifyFile.id, reclassifyType);
      toast.success("Document category updated");
      const filesRes = await getStudentFiles(studentId);
      setFiles(filesRes.data);
      setReclassifyFile(null);
    } catch (err) {
      toast.error("Failed to update category");
    } finally {
      setSavingReclassify(false);
    }
  };

  const handleViewFile = (file) => {
    const ext = (file.filename || "").split(".").pop().toLowerCase();
    const viewableInBrowser = ["pdf", "png", "jpg", "jpeg", "gif", "webp", "svg", "txt"];
    const token = localStorage.getItem("token");
    const baseUrl = process.env.REACT_APP_BACKEND_URL;
    const viewUrl = `${baseUrl}/api/files/view/${file.id}?token=${token}`;

    if (viewableInBrowser.includes(ext)) {
      // Open in modal for PDFs and images
      setViewingFile({ ...file, url: viewUrl, ext });
    } else {
      // For .doc/.docx, open in a new tab via a real anchor (window.open is
      // silently blocked by popup blockers / Safari inside handlers).
      openInNewTab(viewUrl);
    }
  };

  const isViewable = (filename) => {
    const ext = (filename || "").split(".").pop().toLowerCase();
    return ["pdf", "png", "jpg", "jpeg", "gif", "webp", "svg", "txt", "doc", "docx"].includes(ext);
  };

  const getFilesByType = (type) => files.filter((f) => f.file_type === type);

  return (
    <div className="space-y-6">
      {/* Google Drive Links */}
      <Card>
        <CardHeader>
          <CardTitle>Document Links</CardTitle>
          <CardDescription>Google Drive links for IEPs, Supporting Documents, and Referrals</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label>IEP Link</Label>
              {editing ? (
                <Input 
                  value={editData.iep_link || ""} 
                  onChange={(e) => setEditData((p) => ({ ...p, iep_link: e.target.value }))} 
                  placeholder="https://drive.google.com/..."
                  data-testid="iep-link-input"
                />
              ) : student.iep_link ? (
                <a href={student.iep_link} target="_blank" rel="noopener noreferrer" 
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-blue-50 text-blue-700 hover:bg-blue-100 border border-blue-200 text-sm font-medium"
                  data-testid="iep-link-button">
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/><polyline points="15 3 21 3 21 9"/><line x1="10" y1="14" x2="21" y2="3"/></svg>
                  Open IEP
                </a>
              ) : (
                <p className="text-sm py-2 text-muted-foreground">No link set</p>
              )}
            </div>
            <div className="space-y-2">
              <Label>Supporting Docs Link</Label>
              {editing ? (
                <Input 
                  value={editData.supporting_docs_link || ""} 
                  onChange={(e) => setEditData((p) => ({ ...p, supporting_docs_link: e.target.value }))} 
                  placeholder="https://drive.google.com/..."
                  data-testid="supporting-docs-link-input"
                />
              ) : student.supporting_docs_link ? (
                <a href={student.supporting_docs_link} target="_blank" rel="noopener noreferrer" 
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-green-50 text-green-700 hover:bg-green-100 border border-green-200 text-sm font-medium"
                  data-testid="supporting-docs-link-button">
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/><polyline points="15 3 21 3 21 9"/><line x1="10" y1="14" x2="21" y2="3"/></svg>
                  Open Supporting Docs
                </a>
              ) : (
                <p className="text-sm py-2 text-muted-foreground">No link set</p>
              )}
            </div>
            <div className="space-y-2">
              <Label>Referrals Link</Label>
              {editing ? (
                <Input 
                  value={editData.referrals_link || ""} 
                  onChange={(e) => setEditData((p) => ({ ...p, referrals_link: e.target.value }))} 
                  placeholder="https://drive.google.com/..."
                  data-testid="referrals-link-input"
                />
              ) : student.referrals_link ? (
                <a href={student.referrals_link} target="_blank" rel="noopener noreferrer" 
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-purple-50 text-purple-700 hover:bg-purple-100 border border-purple-200 text-sm font-medium"
                  data-testid="referrals-link-button">
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/><polyline points="15 3 21 3 21 9"/><line x1="10" y1="14" x2="21" y2="3"/></svg>
                  Open Referrals
                </a>
              ) : (
                <p className="text-sm py-2 text-muted-foreground">No link set</p>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Upload Documents */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Upload Documents</CardTitle>
              <CardDescription>IEPs, Progress Reports, Clinical Reports, etc.</CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex gap-4 items-end">
            <div className="flex-1 space-y-2">
              <Label>Document Type</Label>
              <Select value={uploadType} onValueChange={setUploadType}>
                <SelectTrigger data-testid="upload-type-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="iep">IEP</SelectItem>
                  <SelectItem value="contract">Contract</SelectItem>
                  <SelectItem value="progress_report">Progress Report</SelectItem>
                  <SelectItem value="proficiency">Proficiency Report</SelectItem>
                  <SelectItem value="clinical_report">Clinical Report</SelectItem>
                  <SelectItem value="supporting_doc">Supporting Document</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <input
              type="file"
              ref={fileInputRef}
              onChange={handleFileUpload}
              className="hidden"
              accept=".pdf,.doc,.docx,.png,.jpg,.jpeg,.gif,.txt"
            />
            <Button onClick={() => fileInputRef.current?.click()} className="bg-[#00305E] hover:bg-[#00305E]/90" data-testid="upload-file-btn">
              Upload File
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* File Type Sections */}
      {[
        { type: "iep", label: "IEPs" },
        { type: "contract", label: "Contracts" },
        { type: "progress_report", label: "Progress Reports" },
        { type: "proficiency", label: "Proficiency Reports" },
        { type: "clinical_report", label: "Clinical Reports" },
        { type: "supporting_doc", label: "Supporting Docs" },
      ].map(({ type, label }) => (
        <Card key={type}>
          <CardHeader>
            <CardTitle>{label}</CardTitle>
          </CardHeader>
          <CardContent>
            {getFilesByType(type).length > 0 ? (
              <div className="space-y-2">
                {getFilesByType(type).map((file) => (
                  <div key={file.id} className="flex items-center justify-between p-3 rounded-lg border">
                    <div className="flex items-center gap-3">
                      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-[#A62E1D]">
                        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/>
                      </svg>
                      <div>
                        <p className="font-medium">{file.filename}</p>
                        <p className="text-xs text-muted-foreground">{new Date(file.uploaded_at).toLocaleDateString()}</p>
                      </div>
                    </div>
                    <div className="flex gap-1">
                      {isViewable(file.filename) && (
                        <Button variant="ghost" size="sm" className="text-blue-600 hover:text-blue-800" onClick={() => handleViewFile(file)} data-testid={`view-${file.id}`}>
                          <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="mr-1"><path d="M2 12s3-7 10-7 10 7 10 7-3 7-10 7-10-7-10-7Z"/><circle cx="12" cy="12" r="3"/></svg>
                          View
                        </Button>
                      )}
                      <Button variant="ghost" size="sm" className="text-slate-600 hover:text-slate-800" onClick={() => openReclassify(file)} data-testid={`reclassify-${file.id}`}>
                        <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="mr-1"><path d="M3 7V5a2 2 0 0 1 2-2h2"/><path d="M17 3h2a2 2 0 0 1 2 2v2"/><path d="M21 17v2a2 2 0 0 1-2 2h-2"/><path d="M7 21H5a2 2 0 0 1-2-2v-2"/><path d="m9 12 2 2 4-4"/></svg>
                        Reclassify
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => handleDownload(file.id, file.filename)} data-testid={`download-${file.id}`}>
                        Download
                      </Button>
                      <Button variant="ghost" size="sm" className="text-red-500 hover:text-red-700" onClick={() => handleDeleteFile(file.id)} data-testid={`delete-file-${file.id}`}>
                        <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 6h18"/><path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6"/><path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2"/></svg>
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-muted-foreground text-center py-4">No files uploaded</p>
            )}
          </CardContent>
        </Card>
      ))}

      {/* Assessment History */}
      <Card>
        <CardHeader>
          <CardTitle>Assessment History</CardTitle>
        </CardHeader>
        <CardContent>
          {assessments.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b">
                    <th className="text-left py-2 px-3">Date</th>
                    <th className="text-left py-2 px-3">Class</th>
                    <th className="text-left py-2 px-3">Participation</th>
                    <th className="text-left py-2 px-3">Behavior</th>
                  </tr>
                </thead>
                <tbody>
                  {assessments.slice(0, 10).map((a, i) => (
                    <tr key={i} className="border-b">
                      <td className="py-2 px-3">{a.date}</td>
                      <td className="py-2 px-3">{a.class_name}</td>
                      <td className="py-2 px-3">{a.participation_level}/4</td>
                      <td className="py-2 px-3">{a.behavior}/4</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="text-muted-foreground text-center py-4">No assessments</p>
          )}
        </CardContent>
      </Card>

      {/* File Viewer Modal */}
      <Dialog open={!!viewingFile} onOpenChange={() => setViewingFile(null)}>
        <DialogContent className="max-w-5xl max-h-[90vh] flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center justify-between">
              <span className="truncate mr-4">{viewingFile?.filename}</span>
              <div className="flex gap-2 shrink-0">
                <Button variant="outline" size="sm" onClick={() => { if (viewingFile) openInNewTab(viewingFile.url); }} data-testid="view-new-tab-btn">
                  <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="mr-1"><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/><polyline points="15 3 21 3 21 9"/><line x1="10" y1="14" x2="21" y2="3"/></svg>
                  Open in New Tab
                </Button>
                <Button variant="outline" size="sm" onClick={() => { if (viewingFile) handleDownload(viewingFile.id, viewingFile.filename); }} data-testid="view-download-btn">
                  Download
                </Button>
              </div>
            </DialogTitle>
          </DialogHeader>
          <div className="flex-1 overflow-hidden rounded-lg border bg-gray-50 min-h-[500px]">
            {viewingFile?.ext === "pdf" ? (
              <iframe src={viewingFile.url} className="w-full h-full min-h-[500px]" title={viewingFile.filename} data-testid="file-viewer-iframe" />
            ) : ["png", "jpg", "jpeg", "gif", "webp", "svg"].includes(viewingFile?.ext) ? (
              <div className="flex items-center justify-center h-full p-4">
                <img src={viewingFile?.url} alt={viewingFile?.filename} className="max-w-full max-h-[70vh] object-contain rounded" data-testid="file-viewer-image" />
              </div>
            ) : viewingFile?.ext === "txt" ? (
              <iframe src={viewingFile?.url} className="w-full h-full min-h-[500px] bg-white p-4" title={viewingFile?.filename} data-testid="file-viewer-text" />
            ) : (
              <div className="flex flex-col items-center justify-center h-full py-12">
                <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className="text-muted-foreground mb-4"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>
                <p className="text-muted-foreground">Preview not available for this file type</p>
                <p className="text-sm text-muted-foreground mt-1">Use &ldquo;Open in New Tab&rdquo; or &ldquo;Download&rdquo; to view</p>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
      {/* Reclassify Category Dialog */}
      <Dialog open={!!reclassifyFile} onOpenChange={(v) => { if (!v) setReclassifyFile(null); }}>
        <DialogContent className="max-w-md" data-testid="reclassify-dialog">
          <DialogHeader>
            <DialogTitle>Reclassify Document</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">
              Change the category for <span className="font-medium text-foreground">{reclassifyFile?.filename}</span>. The file itself is not moved or re-uploaded — only its category label changes.
            </p>
            <div className="space-y-2">
              <Label>Category</Label>
              <Select value={reclassifyType} onValueChange={setReclassifyType}>
                <SelectTrigger data-testid="reclassify-category-select">
                  <SelectValue placeholder="Select category…" />
                </SelectTrigger>
                <SelectContent>
                  {FILE_CATEGORIES.map((c) => (
                    <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setReclassifyFile(null)} data-testid="reclassify-cancel-btn">Cancel</Button>
              <Button
                className="bg-[#00305E] hover:bg-[#00305E]/90"
                disabled={savingReclassify || !reclassifyType || reclassifyType === reclassifyFile?.file_type}
                onClick={handleReclassify}
                data-testid="reclassify-save-btn"
              >
                {savingReclassify ? "Saving…" : "Save Category"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
